import React, { useCallback, useEffect, useState, useRef, Fragment, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Crown, Building2, Users, Star, Receipt, Info, FileText, HandCoins, Globe, GraduationCap, Landmark, Sparkles, Play, Pause, SkipForward, Languages, ShieldCheck, Tv, Wind, Bus, HeartHandshake, BookOpen, Palette, Link2 } from 'lucide-react';
import { SlideAtmosphere } from '../components/deck/SlideAtmosphere';
import { PunjabiPlacard } from '../components/deck/PunjabiPlacard';
import { DelayedVideo } from '../components/deck/DelayedVideo';
const FlippableCard = ({
  card,
  isActive,
  onClick
}: {
  card: any;
  isActive?: boolean;
  onClick?: () => void;
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  // Reset flip state when card becomes inactive
  useEffect(() => {
    if (isActive === false) {
      setIsFlipped(false);
    }
  }, [isActive]);
  return <div className="aspect-[3/4] relative cursor-pointer group" style={{
    perspective: '1000px'
  }} onClick={() => {
    if (onClick) onClick();
    if (isActive !== false) setIsFlipped(!isFlipped);
  }} data-id="element-7303">
      <motion.div className="w-full h-full relative" animate={{
      rotateY: isFlipped ? 180 : 0
    }} transition={{
      duration: 0.8,
      type: 'spring',
      stiffness: 200,
      damping: 25
    }} style={{
      transformStyle: 'preserve-3d'
    }} data-id="element-7304">
        {/* Front */}
        <div className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden shadow-[0_0_30px_rgba(212,175,55,0.15)] bg-black" style={{
        backfaceVisibility: 'hidden'
      }} data-id="element-7305">
          <img src={card.src} alt={card.name} className="w-full h-full object-cover" data-id="element-7306" />
        </div>

        {/* Back */}
        <div className="absolute inset-0 bg-[#0a0604] border border-[#D4AF37]/40 flex flex-col items-center justify-center p-5 text-center shadow-[inset_0_0_40px_rgba(0,0,0,0.8)]" style={{
        transform: 'rotateY(180deg)',
        backfaceVisibility: 'hidden'
      }} data-id="element-7307">
          <div className="absolute inset-0 opacity-10 bg-[url('https://cdn.magicpatterns.com/uploads/3hBK2oMvmeZZy5P54xmeKQ/Lahore_Court_Collection.png')] bg-cover bg-center mix-blend-overlay" data-id="element-7308" />
          <div className="relative z-10 w-full flex flex-col items-center" data-id="element-7309">
            <Crown className="w-5 h-5 text-[#D4AF37] mx-auto mb-2 opacity-80 shrink-0" data-id="element-7310" />
            <h4 className="text-[#FDFBF7] font-serif text-base sm:text-lg mb-2 shrink-0" data-id="element-7311">
              {card.name}
            </h4>
            <div className="w-12 h-px bg-[#D4AF37]/50 mx-auto mb-3 shrink-0" data-id="element-7312" />
            <p className="text-white/75 text-[0.62rem] sm:text-[0.7rem] font-light leading-relaxed mb-3 text-left" data-id="element-7313">
              {card.desc}
            </p>
            {card.stats && <div className="grid grid-cols-2 gap-3 text-left bg-black/40 p-2.5 border border-[#D4AF37]/20 backdrop-blur-sm w-full shrink-0" data-id="element-7314">
                {card.stats.map((stat: any, idx: number) => <div key={idx} data-id="element-7315">
                    <p className="text-[#D4AF37] text-[9px] uppercase tracking-widest mb-0.5" data-id="element-7316">
                      {stat.label}
                    </p>
                    <p className="text-white/90 text-xs font-light" data-id="element-7317">
                      {stat.value}
                    </p>
                  </div>)}
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
};
const TreasuryFan = ({
  cards
}: {
  cards: any[];
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  return <div className="relative w-full max-w-4xl mx-auto h-[320px] flex justify-center items-end" data-id="element-7318">
      {/* Backdrop to dismiss active card */}
      <AnimatePresence data-id="element-7319">
        {activeIndex !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm cursor-pointer" onClick={() => setActiveIndex(null)} data-id="element-7320" />}
      </AnimatePresence>

      {cards.map((card, i) => {
      const isActive = activeIndex === i;
      const offset = i - Math.floor(cards.length / 2);
      const rotation = isActive ? 0 : offset * 12;
      const yOffset = isActive ? -120 : Math.abs(offset) * 15;
      // Use window.innerWidth to determine if mobile for xOffset, or just use a smaller base offset
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
      const xOffset = isActive ? 0 : offset * (isMobile ? 40 : 80);
      const zIndex = isActive ? 50 : 10 + i;
      return <motion.div key={i} className="absolute bottom-0 w-32 sm:w-48 md:w-56" style={{
        zIndex
      }} animate={{
        rotate: rotation,
        y: yOffset,
        scale: isActive ? isMobile ? 1.2 : 1.4 : 1,
        x: xOffset
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 25
      }} data-id="element-7321">
            <FlippableCard card={card} isActive={isActive} onClick={() => {
          if (!isActive) {
            setActiveIndex(i);
          }
        }} data-id="element-7322" />
          </motion.div>;
    })}
    </div>;
};
const FounderCoinCarousel = ({
  coins
}: {
  coins: {
    src: string;
    label: string;
  }[];
}) => {
  const [index, setIndex] = useState(0);
  const go = (dir: number) => setIndex(prev => (prev + dir + coins.length) % coins.length);
  return <div className="w-full flex flex-col items-center" data-id="element-7323">
      <div className="relative w-full max-w-4xl px-4 sm:px-0" data-id="element-7324">
        {/* Large coin image */}
        <div className="relative aspect-square sm:aspect-[3/2] w-full overflow-hidden border border-[#D4AF37]/20 bg-black shadow-[0_0_60px_rgba(212,175,55,0.15)]" data-id="element-7325">
          <AnimatePresence mode="wait" data-id="element-7326">
            <motion.img key={index} src={coins[index].src} alt={coins[index].label} initial={{
            opacity: 0,
            scale: 1.02
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} className="absolute inset-0 w-full h-full object-contain p-4 sm:p-0" data-id="element-7327" />
          </AnimatePresence>
        </div>

        {/* Arrows */}
        <button type="button" onClick={() => go(-1)} aria-label="Previous coin" className="absolute left-6 sm:-left-6 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/40 bg-black/70 backdrop-blur-sm flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors" data-id="element-7328">
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-7329" />
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Next coin" className="absolute right-6 sm:-right-6 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/40 bg-black/70 backdrop-blur-sm flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors" data-id="element-7330">
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-7331" />
        </button>
      </div>

      {/* Caption + dots */}
      <p className="text-[#D4AF37] text-[10px] sm:text-sm tracking-[0.2em] uppercase mt-5 text-center px-4" data-id="element-7332">
        {coins[index].label}
      </p>
      <div className="flex items-center gap-2 mt-3" data-id="element-7333">
        {coins.map((_, i) => <button key={i} type="button" onClick={() => setIndex(i)} aria-label={`View coin ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4 sm:w-6 bg-[#D4AF37]' : 'w-1.5 bg-white/30 hover:bg-white/50'}`} data-id="element-7334" />)}
      </div>
    </div>;
};
const DailyPacketAnimation = () => {
  const [packetState, setPacketState] = useState<'unopened' | 'opening' | 'revealed'>('unopened');
  return <div className="flex flex-col items-center justify-center w-full h-[560px] relative" data-id="element-7335">
      <AnimatePresence mode="wait" data-id="element-7336">
        {packetState === 'unopened' && <motion.div key="unopened" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 1.1
      }} className="flex flex-col items-center text-center max-w-md mx-auto relative" data-id="element-7337">
            <motion.div className="relative w-64 sm:w-72 mb-8" data-id="element-7338">
              <motion.div animate={{
            scale: [1, 1.05, 1],
            opacity: [0.5, 0.8, 0.5]
          }} transition={{
            duration: 3,
            repeat: Infinity
          }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.35),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-7339" />
              <motion.button type="button" onClick={() => {
            setPacketState('opening');
            setTimeout(() => setPacketState('revealed'), 1900);
          }} whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.96
          }} className="relative block w-full cursor-pointer" data-id="element-7340">
                <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope" className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]" data-id="element-7341" />
                <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap" data-id="element-7342">
                  Click to open
                </span>
              </motion.button>
            </motion.div>
          </motion.div>}

        {packetState === 'opening' && <motion.div key="opening" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative flex items-center justify-center w-full h-full" data-id="element-7343">
            <motion.div initial={{
          scale: 0.4,
          opacity: 0
        }} animate={{
          scale: 4,
          opacity: [0, 0.9, 0]
        }} transition={{
          duration: 1.8,
          ease: 'easeOut'
        }} className="absolute w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl" data-id="element-7344" />
            <motion.div initial={{
          scale: 1,
          opacity: 1
        }} animate={{
          scale: [1, 1.1, 0.6],
          opacity: [1, 1, 0],
          rotateZ: [0, -3, 4, -2, 0]
        }} transition={{
          duration: 1.8,
          times: [0, 0.5, 1],
          ease: 'easeInOut'
        }} className="relative w-64 sm:w-72 z-10" data-id="element-7345">
              <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Opening envelope" className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]" data-id="element-7346" />
            </motion.div>
            {Array.from({
          length: 20
        }).map((_, i) => {
          const angle = i / 20 * Math.PI * 2;
          const distance = 280;
          return <motion.div key={i} initial={{
            x: 0,
            y: 0,
            opacity: 0,
            scale: 0
          }} animate={{
            x: Math.cos(angle) * distance,
            y: Math.sin(angle) * distance,
            opacity: [0, 1, 0],
            scale: [0, 1.2, 0]
          }} transition={{
            duration: 1.6,
            delay: 0.3 + i % 4 * 0.05,
            ease: 'easeOut'
          }} className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_14px_rgba(212,175,55,1)]" data-id="element-7347" />;
        })}
          </motion.div>}

        {packetState === 'revealed' && <motion.div key="revealed" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center" data-id="element-7348">
            <div className="flex justify-center mb-8" data-id="element-7349">
              <motion.div initial={{
            y: 40,
            opacity: 0,
            rotate: 0
          }} animate={{
            y: 0,
            opacity: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            delay: 0.1
          }} className="w-48 aspect-[3/4]" data-id="element-7350">
                <img src="https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png" className="w-full h-full object-cover shadow-2xl" alt="Card" data-id="element-7351" />
              </motion.div>
            </div>
            <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1
        }} onClick={() => setPacketState('unopened')} className="px-6 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 transition-colors" data-id="element-7352">
              Reset Animation
            </motion.button>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const SponsorDeckPage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [archiveIndex, setArchiveIndex] = useState(0);
  const [drinkIndex, setDrinkIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [slide7AudioPlaying, setSlide7AudioPlaying] = useState(false);
  const slide7AudioRef = useRef<HTMLAudioElement>(null);
  const slide7VideoRef = useRef<HTMLVideoElement>(null);
  const slide7TimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [slide7bAudioPlaying, setSlide7bAudioPlaying] = useState(false);
  const slide7bAudioRef = useRef<HTMLAudioElement>(null);
  const slide7bVideoRef = useRef<HTMLVideoElement>(null);
  const slide7bTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSlide7Play = () => {
    if (slide7AudioPlaying) {
      // Pause
      if (slide7AudioRef.current) slide7AudioRef.current.pause();
      if (slide7VideoRef.current) slide7VideoRef.current.pause();
      if (slide7TimeoutRef.current) clearTimeout(slide7TimeoutRef.current);
      setSlide7AudioPlaying(false);
    } else {
      // Play
      if (isPlaying) {
        if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
      }
      if (slide7AudioRef.current) {
        slide7AudioRef.current.play();
        setSlide7AudioPlaying(true);
        if (slide7VideoRef.current) {
          if (slide7VideoRef.current.currentTime === 0) {
            slide7TimeoutRef.current = setTimeout(() => {
              if (slide7VideoRef.current) {
                slide7VideoRef.current.playbackRate = 0.5;
                slide7VideoRef.current.play();
              }
            }, 10000);
          } else {
            slide7VideoRef.current.playbackRate = 0.5;
            slide7VideoRef.current.play();
          }
        }
      }
    }
  };
  const handleSlide7bPlay = () => {
    if (slide7bAudioPlaying) {
      // Pause
      if (slide7bAudioRef.current) slide7bAudioRef.current.pause();
      if (slide7bVideoRef.current) slide7bVideoRef.current.pause();
      if (slide7bTimeoutRef.current) clearTimeout(slide7bTimeoutRef.current);
      setSlide7bAudioPlaying(false);
    } else {
      // Play
      if (isPlaying) {
        if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
      }
      if (slide7bAudioRef.current) {
        slide7bAudioRef.current.play();
        setSlide7bAudioPlaying(true);
        if (slide7bVideoRef.current) {
          if (slide7bVideoRef.current.currentTime === 0) {
            slide7bTimeoutRef.current = setTimeout(() => {
              if (slide7bVideoRef.current) {
                slide7bVideoRef.current.playbackRate = 0.5;
                slide7bVideoRef.current.play();
              }
            }, 10000);
          } else {
            slide7bVideoRef.current.playbackRate = 0.5;
            slide7bVideoRef.current.play();
          }
        }
      }
    }
  };
  useEffect(() => {
    const video = slide7VideoRef.current;
    if (!video) return;
    let reverseIntervalId: ReturnType<typeof setInterval>;
    let reverseTimeoutId: ReturnType<typeof setTimeout>;
    const reversePlay = () => {
      if (!video) return;
      if (video.currentTime <= 0.1) {
        // Reached the beginning, stop reversing and play forward again
        clearInterval(reverseIntervalId);
        video.playbackRate = 0.5;
        video.play();
        return;
      }
      // Only step back if the video isn't currently busy seeking
      // This prevents Chrome from stalling when stepping backwards
      if (!video.seeking) {
        // Step backwards by 0.05s every 100ms (0.5x speed)
        video.currentTime = Math.max(0, video.currentTime - 0.05);
      }
    };
    const handleEnded = () => {
      video.pause(); // Ensure forward playback is stopped
      // Wait 5 seconds before reversing
      reverseTimeoutId = setTimeout(() => {
        reverseIntervalId = setInterval(reversePlay, 100);
      }, 5000);
    };
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
      if (reverseIntervalId) clearInterval(reverseIntervalId);
      if (reverseTimeoutId) clearTimeout(reverseTimeoutId);
    };
  }, []);
  useEffect(() => {
    const video = slide7bVideoRef.current;
    if (!video) return;
    let reverseIntervalId: ReturnType<typeof setInterval>;
    let reverseTimeoutId: ReturnType<typeof setTimeout>;
    const reversePlay = () => {
      if (!video) return;
      if (video.currentTime <= 0.1) {
        // Reached the beginning, stop reversing and play forward again
        clearInterval(reverseIntervalId);
        video.playbackRate = 0.5;
        video.play();
        return;
      }
      // Only step back if the video isn't currently busy seeking
      // This prevents Chrome from stalling when stepping backwards
      if (!video.seeking) {
        // Step backwards by 0.05s every 100ms (0.5x speed)
        video.currentTime = Math.max(0, video.currentTime - 0.05);
      }
    };
    const handleEnded = () => {
      video.pause(); // Ensure forward playback is stopped
      // Wait 5 seconds before reversing
      reverseTimeoutId = setTimeout(() => {
        reverseIntervalId = setInterval(reversePlay, 100);
      }, 5000);
    };
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
      if (reverseIntervalId) clearInterval(reverseIntervalId);
      if (reverseTimeoutId) clearTimeout(reverseTimeoutId);
    };
  }, []);
  const tracks = ['https://cdn.magicpatterns.com/uploads/i6gGeaSuUDdxcvUXNfouoe/Dusk_at_the_Walled_Gate.mp3', 'https://cdn.magicpatterns.com/uploads/xqFvTV8Sq5Sxo7k3BZ1pu2/2._Maharaja_Ranjit_Singh_Invite_to_Enter_Quest__Lion_of_the_Five_Rivers.mp3'];
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const nextTrack = () => {
    setCurrentTrackIndex(prev => (prev + 1) % tracks.length);
    setIsPlaying(true);
  };
  useEffect(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.play().catch(() => setIsPlaying(false));
    }
  }, [currentTrackIndex]);
  const totalSlides = 33;
  const nextSlide = useCallback(() => {
    setActiveSlide(prev => Math.min(prev + 1, totalSlides - 1));
  }, []);
  const prevSlide = useCallback(() => {
    setActiveSlide(prev => Math.max(prev - 1, 0));
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  useEffect(() => {
    const archiveInterval = setInterval(() => {
      setArchiveIndex(prev => (prev + 1) % 4);
    }, 5000);
    const drinkInterval = setInterval(() => {
      setDrinkIndex(prev => (prev + 1) % 5);
    }, 3000);
    return () => {
      clearInterval(archiveInterval);
      clearInterval(drinkInterval);
    };
  }, []);
  return <div className="bg-black w-screen h-screen overflow-hidden text-white relative font-sans" data-id="element-7353">
      {/* Fixed Chrome */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-[#D4AF37] origin-left z-50" initial={{
      scaleX: 0
    }} animate={{
      scaleX: activeSlide / (totalSlides - 1)
    }} transition={{
      duration: 0.5,
      ease: 'easeInOut'
    }} data-id="element-7354" />

      <Link to="/" className="fixed top-8 left-8 z-40 flex items-center gap-3 text-white/50 hover:text-[#D4AF37] transition-colors text-xs tracking-widest uppercase font-light" data-id="element-7355">
        <ArrowLeft className="w-4 h-4" data-id="element-7356" />
        Home
      </Link>

      <div className="fixed top-4 sm:top-8 right-4 sm:right-8 z-40 flex flex-col items-end gap-2 sm:gap-3" data-id="element-7357">
        <div className="text-[#D4AF37]/60 text-[10px] sm:text-xs font-light tracking-[0.3em]" data-id="element-7358">
          {String(activeSlide + 1).padStart(2, '0')} / {totalSlides}
        </div>
        <div className="flex items-center gap-2" data-id="element-7359">
          <button onClick={togglePlay} className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 transition-all duration-300" data-id="element-7360">
            {isPlaying ? <Pause className="w-2 h-2 sm:w-3 sm:h-3" data-id="element-7361" /> : <Play className="w-2 h-2 sm:w-3 sm:h-3 ml-0.5" data-id="element-7362" />}
          </button>
          <button onClick={nextTrack} className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 transition-all duration-300" data-id="element-7363">
            <SkipForward className="w-2 h-2 sm:w-3 sm:h-3" data-id="element-7364" />
          </button>
        </div>
      </div>

      <audio ref={audioRef} src={tracks[currentTrackIndex]} loop className="hidden" data-id="element-7365" />

      <div className="fixed bottom-4 sm:bottom-8 right-4 sm:right-8 z-40 hidden sm:flex items-center gap-4" data-id="element-7366">
        <button onClick={prevSlide} disabled={activeSlide === 0} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-20 transition-all duration-300" data-id="element-7367">
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-7368" />
        </button>
        <button onClick={nextSlide} disabled={activeSlide === totalSlides - 1} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-20 transition-all duration-300" data-id="element-7369">
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-7370" />
        </button>
      </div>

      {/* Slides Container */}
      <motion.div className="flex h-full w-full" drag="x" dragConstraints={{
      left: 0,
      right: 0
    }} dragElastic={0.2} onDragEnd={(e, {
      offset,
      velocity
    }) => {
      const swipeThreshold = 50;
      if (offset.x < -swipeThreshold || velocity.x < -500) nextSlide();else if (offset.x > swipeThreshold || velocity.x > 500) prevSlide();
    }} animate={{
      x: `-${activeSlide * 100}vw`
    }} transition={{
      type: 'spring',
      stiffness: 150,
      damping: 25,
      mass: 0.8
    }} data-id="element-7371">
        {/* 1. Cover */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7372">
          <SlideAtmosphere imageUrl="https://cdn.magicpatterns.com/uploads/7ngvtUG3Hhfrt7dJK3EcMQ/Gemini_Generated_Image_b3829nb3829nb382.png" opacity={0.5} overlayGradient="gold" animate="ken-burns-loop" withParticles data-id="element-7373" />
          <PunjabiPlacard word="ਸ਼ੁਰੂਆਤ" translation="Beginning" data-id="element-7374" />

          <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl mx-auto" data-id="element-7375">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} data-id="element-7376">
              <h1 className="font-serif text-5xl sm:text-7xl md:text-8xl text-[#FDFBF7] mb-4 sm:mb-6 leading-none drop-shadow-2xl" data-id="element-7377">
                Kingdoms of Punjab
              </h1>
              <p className="text-sm sm:text-xl md:text-2xl text-[#D4AF37] font-light tracking-[0.2em] uppercase mb-6 sm:mb-8" data-id="element-7378">
                Life in the Sikh Kingdoms
              </p>
              <div className="w-px h-12 sm:h-16 bg-gradient-to-b from-[#D4AF37] to-transparent mx-auto" data-id="element-7379" />
            </motion.div>
          </div>
        </section>

        {/* 2. Opening Truth */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7380">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7381" />
          <PunjabiPlacard word="ਵਿਰਾਸਤ" translation="Heritage" data-id="element-7382" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7383">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7384">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7385">
                We inherited a civilization.
                <br data-id="element-7386" />
                <span className="text-[#D4AF37]" data-id="element-7387">
                  Our children inherited fragments.
                </span>
              </h2>
              <p className="text-lg text-white/60 font-light max-w-2xl mx-auto" data-id="element-7388">
                When a people forget their history, they risk losing the courage
                and dignity it was meant to give them.
              </p>
            </motion.div>

            <motion.figure initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5,
            delay: 0.3
          }} className="mt-16 pt-10 border-t border-[#D4AF37]/15" data-id="element-7389">
              <blockquote className="font-serif text-xl sm:text-2xl text-[#D4AF37] italic leading-relaxed" data-id="element-7390">
                "A people without knowledge of their past history, origin and
                culture is like a tree without roots."
              </blockquote>
              <figcaption className="text-white/40 text-xs tracking-[0.3em] uppercase mt-6" data-id="element-7391">
                — Winston Churchill
              </figcaption>
            </motion.figure>
          </div>
        </section>

        {/* 3. ROM Legacy */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7392">
          <SlideAtmosphere imageUrl="https://cdn.magicpatterns.com/uploads/mdkRi9opWdPpM6cSCo7Zgm/image.png" opacity={0.2} overlayGradient="dark" data-id="element-7393" />
          <PunjabiPlacard word="ਪ੍ਰਮਾਣ" translation="Proof" data-id="element-7394" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center gap-16" data-id="element-7395">
            <motion.div className="flex-1" initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7396">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6" data-id="element-7397">
                The Precedent
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-6" data-id="element-7398">
                Our community has proven this story matters.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7399">
                <span className="text-[#D4AF37]" data-id="element-7400">
                  Arts of the Sikh Kingdoms
                </span>{' '}
                remains the second most successful exhibition in Canadian
                history at the Royal Ontario Museum.
              </p>
            </motion.div>
            <motion.div className="flex-1 w-full max-w-sm" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7401">
              <div className="aspect-[3/4] p-3 bg-[#110c08] border border-[#D4AF37]/20 shadow-[0_20px_50px_rgba(0,0,0,0.5)]" data-id="element-7402">
                <img src="https://cdn.magicpatterns.com/uploads/mdkRi9opWdPpM6cSCo7Zgm/image.png" alt="ROM Exhibition" className="w-full h-full object-cover transition-all duration-700 hover:scale-105" data-id="element-7403" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 4. Epiphany & Trailer */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex flex-col items-center justify-center" data-id="element-7404">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7405" />
          <PunjabiPlacard word="ਜਾਗ੍ਰਿਤੀ" translation="Awakening" data-id="element-7406" />

          <div className="relative z-10 max-w-6xl mx-auto w-full px-6 text-center" data-id="element-7407">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7408">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-12" data-id="element-7409">
                What if Sikh history could feel alive?
              </h2>
              <div className="w-full max-w-4xl mx-auto aspect-video bg-black border border-[#D4AF37]/20 shadow-[0_0_50px_rgba(212,175,55,0.1)] relative p-1" data-id="element-7410">
                <iframe src="https://www.youtube.com/embed/2yMWvJinVgk" title="Trailer" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" allowFullScreen className="w-full h-full" data-id="element-7411" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 5. Vision */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7412">
          <SlideAtmosphere imageUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" opacity={0.15} overlayGradient="gold" data-id="element-7413" />
          <PunjabiPlacard word="ਮੰਜ਼ਿਲ" translation="Destination" data-id="element-7414" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7415">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7416">
              <p className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-8" data-id="element-7417">
                The Goal
              </p>
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7418">
                The most successful immersive experience in Canadian history.
              </h2>
              <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/50 to-transparent mx-auto" data-id="element-7419" />
            </motion.div>
          </div>
        </section>

        {/* 6. Step Inside */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7420">
          <SlideAtmosphere videoUrl="https://cdn.magicpatterns.com/uploads/2vbujNeh6XCJnC6Xt6Yz87/animate_this_image_so_its_like.mp4" opacity={1} overlayGradient="dark" lightScrim videoBrightness={1.6} playbackRate={0.5} data-id="element-7421" />
          <PunjabiPlacard word="ਦਰਬਾਰ" translation="Court" data-id="element-7422" />

          <div className="absolute bottom-24 left-0 right-0 z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7423">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7424">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#FDFBF7] leading-tight drop-shadow-[0_10px_30px_rgba(0,0,0,0.8)]" data-id="element-7425">
                Step into a world of empire, diplomacy, art, and courage.
              </h2>
            </motion.div>
          </div>
        </section>

        {/* 7. Portraits That Awaken */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7426">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7427" />
          <PunjabiPlacard word="ਦਰਸ਼ਨ" translation="Vision" data-id="element-7428" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row items-center gap-12" data-id="element-7429">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7430">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7431">
                Historical figures come to life.
              </h2>
              <p className="text-lg text-white/60 font-light mb-8" data-id="element-7432">
                Maharaja Ranjit Singh and others awaken on screen to guide
                students through the Darbar.
              </p>

              <div className="flex items-center gap-4" data-id="element-7433">
                <button onClick={handleSlide7Play} className="w-14 h-14 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/20 hover:scale-105 transition-all shrink-0" data-id="element-7434">
                  {slide7AudioPlaying ? <Pause className="w-6 h-6" data-id="element-7435" /> : <Play className="w-6 h-6 ml-1" data-id="element-7436" />}
                </button>
                <AnimatePresence data-id="element-7437">
                  {slide7AudioPlaying && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -10
                }} className="flex flex-col" data-id="element-7438">
                      <p className="text-[#D4AF37] text-xs tracking-widest uppercase font-light mb-1" data-id="element-7439">
                        Now Playing
                      </p>
                      <p className="text-white/80 text-sm font-serif italic" data-id="element-7440">
                        Awakening of the Veiled Lioness
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <audio ref={slide7AudioRef} src="https://cdn.magicpatterns.com/uploads/fHMFP9bk9qh56JpvKMc9vM/Awakening_of_the_Veiled_Lioness_(2).mp3" className="hidden" data-id="element-7441" />
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7442">
              <div className="h-[80vh] max-h-[800px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7443">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7444">
                  <video ref={slide7VideoRef} src="https://cdn.magicpatterns.com/uploads/oAYvh93WVHg7sLuQmvLMws/WhatsApp%20Video%202026-06-08%20at%2009.07.35.mp4" muted playsInline className="w-full h-full object-cover" data-id="element-7445" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 7b. Paintings Become Portals */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7446">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7447" />
          <PunjabiPlacard word="ਕਲਾ" translation="Art" data-id="element-7448" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-7449">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7450">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7451">
                Paintings become portals.
              </h2>
              <p className="text-lg text-white/60 font-light mb-8" data-id="element-7452">
                Static archival art transforms into living, breathing figures
                right before their eyes.
              </p>

              <div className="flex items-center gap-4" data-id="element-7453">
                <button onClick={handleSlide7bPlay} className="w-14 h-14 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/20 hover:scale-105 transition-all shrink-0" data-id="element-7454">
                  {slide7bAudioPlaying ? <Pause className="w-6 h-6" data-id="element-7455" /> : <Play className="w-6 h-6 ml-1" data-id="element-7456" />}
                </button>
                <AnimatePresence data-id="element-7457">
                  {slide7bAudioPlaying && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -10
                }} className="flex flex-col" data-id="element-7458">
                      <p className="text-[#D4AF37] text-xs tracking-widest uppercase font-light mb-1" data-id="element-7459">
                        Now Playing
                      </p>
                      <p className="text-white/80 text-sm font-serif italic" data-id="element-7460">
                        Awakening of the Maharaja
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <audio ref={slide7bAudioRef} src="https://cdn.magicpatterns.com/uploads/6aVtpiwFTh4Fdg7GirPpMC/Awakening_of_the_Maharaja.mp3" className="hidden" data-id="element-7461" />
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7462">
              <div className="h-[80vh] max-h-[800px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7463">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7464">
                  <video ref={slide7bVideoRef} src="https://cdn.magicpatterns.com/uploads/4rTD4YeXmjgXGTejYDnVus/WhatsApp%20Video%202026-05-21%20at%2016.14.44%20(1).mp4" muted playsInline className="w-full h-full object-cover" data-id="element-7465" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 8. History Speaks Back */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7466">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7467" />
          <PunjabiPlacard word="ਸੰਵਾਦ" translation="Dialogue" data-id="element-7468" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-7469">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7470">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#FDFBF7] mb-4 sm:mb-5" data-id="element-7471">
                From watching history to speaking with it.
              </h2>
              <p className="text-base sm:text-lg text-white/60 font-light mb-8" data-id="element-7472">
                Using live avatar technology, students ask questions and receive
                historically informed responses in real time.
              </p>

              {/* Trilingual */}
              <div className="mb-7" data-id="element-7473">
                <div className="flex items-center gap-2 mb-3 justify-center md:justify-start" data-id="element-7474">
                  <Languages className="w-4 h-4 text-[#D4AF37]" data-id="element-7475" />
                  <p className="text-[#D4AF37] text-[0.7rem] tracking-[0.25em] uppercase" data-id="element-7476">
                    Answers in three languages
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-id="element-7477">
                  {['English', 'ਪੰਜਾਬੀ', 'Français'].map(lang => <span key={lang} className="px-3 py-1.5 text-xs sm:text-sm font-light text-[#FDFBF7] border border-[#D4AF37]/30 rounded-full bg-white/5" data-id="element-7478">
                      {lang}
                    </span>)}
                </div>
              </div>

              {/* AI Guardrails */}
              <div className="mb-7 flex items-start gap-3 text-left border-l-2 border-[#D4AF37]/30 pl-4" data-id="element-7479">
                <ShieldCheck className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-7480" />
                <div data-id="element-7481">
                  <p className="text-[#FDFBF7] text-sm font-medium mb-1" data-id="element-7482">
                    Controlled, responsible AI
                  </p>
                  <p className="text-white/55 font-light text-xs sm:text-sm leading-relaxed" data-id="element-7483">
                    Every response is bounded by a curated, historian-verified
                    knowledge base with strict guardrails — keeping answers
                    accurate, age-appropriate, and culturally respectful. No
                    hallucinations, no anachronisms.
                  </p>
                </div>
              </div>

              {/* Technology Partners */}
              <div className="pt-5 border-t border-white/10" data-id="element-7484">
                <div className="flex items-center gap-2 mb-3 justify-center md:justify-start" data-id="element-7485">
                  <Tv className="w-4 h-4 text-[#D4AF37]" data-id="element-7486" />
                  <p className="text-[#D4AF37] text-[0.7rem] tracking-[0.25em] uppercase" data-id="element-7487">
                    Technology Partners
                  </p>
                </div>
                <div className="flex items-center gap-5 justify-center md:justify-start mb-2" data-id="element-7488">
                  <span className="font-serif text-xl sm:text-2xl tracking-[0.15em] text-[#FDFBF7]" data-id="element-7489">
                    SAMSUNG
                  </span>
                </div>
                <p className="text-white/45 font-light text-xs leading-relaxed max-w-md mx-auto md:mx-0" data-id="element-7490">
                  Samsung has offered to support the initiative with their frame
                  displays and immersive screen technology.
                </p>
              </div>
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7491">
              <div className="h-[80vh] max-h-[800px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7492">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7493">
                  <video src="https://cdn.magicpatterns.com/uploads/tHe5vNNjxpJUtnsEoB8t32/WhatsApp%20Video%202026-05-26%20at%2015.06.40.mp4" playsInline controls className="w-full h-full object-cover" data-id="element-7494" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 9. Living Archive */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7495">
          <div className="absolute inset-0 overflow-hidden" data-id="element-7496">
            <AnimatePresence mode="popLayout" data-id="element-7497">
              <motion.img key={archiveIndex} src={['https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2uRFx3az1Ndo6QPmwLotNT/Gemini_Generated_Image_6xdim76xdim76xdi.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/wVBxvpncFWjZ3U7V1C4TbV/Gemini_Generated_Image_cc8os5cc8os5cc8o.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/iav6C11v1iAqRNyzsLVGur/Gemini_Generated_Image_ouvfm7ouvfm7ouvf.png'][archiveIndex]} initial={{
              opacity: 0,
              x: 100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} transition={{
              duration: 1.5,
              ease: 'easeInOut'
            }} className="absolute inset-0 w-full h-full object-cover" data-id="element-7498" />
            </AnimatePresence>
          </div>
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7499" />
          <PunjabiPlacard word="ਯਾਦ" translation="Memory" data-id="element-7500" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7501">
            <motion.div className="text-center" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7502">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] mb-6 drop-shadow-2xl" data-id="element-7503">
                A living archive of memories.
              </h2>
              <p className="text-xl text-white/90 font-light drop-shadow-lg" data-id="element-7504">
                Cinematic scenes of battles, diplomacy, and sacred spaces.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 10. Scent, Sound, Taste */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7505">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7506" />
          <PunjabiPlacard word="ਅਹਿਸਾਸ" translation="Feeling" data-id="element-7507" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7508">
            <motion.div className="text-center mb-16" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7509">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7510">
                Not just seen. Felt.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7511">
                Sound, scent, and taste make history unforgettable.
              </p>
              <p className="text-[#D4AF37]/80 text-xs sm:text-sm tracking-[0.25em] uppercase mt-4" data-id="element-7512">
                Every drink poured from ingredients of the era · Every scent
                researched from the period
              </p>
            </motion.div>

            <div className="flex justify-center items-center h-[50vh]" data-id="element-7513">
              {[{
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/e7tNmw42aGJMhG2K8pg9sq/Sher-e-Punjab_Lassi_%28Lion_of_Punjab_Smoothie%29.webp',
              name: 'Sher-e-Punjab Lassi',
              inspiredBy: 'Inspired by Maharaja Ranjit Singh',
              scent: 'Cardamom, saffron & sweet cream',
              era: 'Saffron carried over the Khyber, churned yogurt of the court'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7s7gbBG7Kx151cwzpHK1du/Hari_Singh_Nalwa%E2%80%99s_Tiger_Fury_Sharbat.webp',
              name: 'Tiger Fury Sharbat',
              inspiredBy: 'Inspired by Hari Singh Nalwa',
              scent: 'Pomegranate, roasted cumin & ginger',
              era: 'Wild pomegranate & tamarind — the warrior sharbat of the frontier'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eKo28eax4tv8HfFsN3KZGY/Chabeel_of_Compassion_%28Rose__Kewra_Cooler%29.webp',
              name: 'Chabeel of Compassion',
              inspiredBy: 'Inspired by Moran Sarkar',
              scent: 'Rose petals & kewra blossom',
              era: 'Rose & kewra essence — the chabeel offered in selfless seva'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uNnPb6Ex8V8D1T89ouiXSm/Shahi_Aam_Panna_%28Royal_Raw_Mango_Punch%29.webp',
              name: 'Shahi Aam Panna',
              inspiredBy: 'Inspired by Maharani Jind Kaur',
              scent: 'Raw mango, fresh mint & black salt',
              era: 'Green mango cooler — the summer drink of the zenana'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pMQJkiKcDmbaWHaNH1zaMn/Pehalwan%E2%80%99s_Punch_%28Banana_Jaggery_Shake%29.webp',
              name: "Pehalwan's Punch",
              inspiredBy: 'Inspired by Punjabi wrestlers',
              scent: 'Banana, jaggery, cardamom & nutmeg',
              era: 'Banana & jaggery shake with yogurt, honey & almonds — the akhara’s strength fuel'
            }].map((drink, i) => {
              const isCenter = i === drinkIndex;
              const offset = (i - drinkIndex + 5) % 5;
              const position = offset >= 3 ? offset - 5 : offset;
              return <motion.div key={i} animate={{
                x: position * 250,
                scale: isCenter ? 1.2 : 0.8,
                opacity: isCenter ? 1 : 0.4,
                zIndex: isCenter ? 20 : 10
              }} transition={{
                duration: 0.8,
                ease: 'easeInOut'
              }} className="absolute text-center w-48 sm:w-56" data-id="element-7514">
                    {/* Inspired by — above the image */}
                    <motion.p animate={{
                  opacity: isCenter ? 1 : 0
                }} transition={{
                  duration: 0.5
                }} className="absolute bottom-full left-0 right-0 mb-3 text-white/80 text-xs italic font-light drop-shadow-md" data-id="element-7515">
                      {drink.inspiredBy}
                    </motion.p>

                    <div className="aspect-[3/4] overflow-hidden border border-[#D4AF37]/40 bg-[#0a0604] relative shadow-2xl" data-id="element-7516">
                      <img src={drink.src} alt={drink.name} className="w-full h-full object-cover" data-id="element-7517" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" data-id="element-7518" />
                    </div>

                    {/* Name + scent — below the image */}
                    <motion.div animate={{
                  opacity: isCenter ? 1 : 0
                }} transition={{
                  duration: 0.5
                }} className="absolute top-full left-0 right-0 mt-4" data-id="element-7519">
                      <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-2 font-medium drop-shadow-md" data-id="element-7520">
                        {drink.name}
                      </p>
                      <div className="flex items-center justify-center gap-2 text-white/55 mb-2" data-id="element-7521">
                        <Wind className="w-3.5 h-3.5 text-[#D4AF37]/70 shrink-0" data-id="element-7522" />
                        <p className="text-[0.7rem] font-light tracking-wide drop-shadow-md" data-id="element-7523">
                          {drink.scent}
                        </p>
                      </div>
                      <p className="text-[0.65rem] font-light italic text-white/40 leading-snug max-w-[14rem] mx-auto drop-shadow-md" data-id="element-7524">
                        {drink.era}
                      </p>
                    </motion.div>
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* 11. In Progress Now */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7525">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7526" />
          <PunjabiPlacard word="ਸਿਰਜਣਾ" translation="Creation" data-id="element-7527" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 w-full py-8" data-id="element-7528">
            <motion.div className="text-center mb-6" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7529">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-2" data-id="element-7530">
                This is no longer just an idea.
              </h2>
              <p className="text-base text-white/60 font-light mb-1" data-id="element-7531">
                Testing the environment, scale, and production pathway.
              </p>
              <p className="text-[0.65rem] text-[#D4AF37] tracking-widest uppercase font-light" data-id="element-7532">
                Live footage from construction at Illuminarium at the
                Distillery, June 2026.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-4" data-id="element-7533">
              {['https://cdn.magicpatterns.com/uploads/a7heywR1FeDYnub9ua9WT9/WhatsApp_Image_2026-06-12_at_14.36.03_(1).jpg', 'https://cdn.magicpatterns.com/uploads/ssJFUVqUnzkDMnb4yqvNvT/WhatsApp_Image_2026-06-13_at_10.33.54.jpg', 'https://cdn.magicpatterns.com/uploads/kQxbd1wr2FNT6U2x9evwFs/WhatsApp_Image_2026-06-12_at_14.36.03_(6).jpg', 'https://cdn.magicpatterns.com/uploads/pnghcT8rVnDqxy4q3pgpBM/WhatsApp_Image_2026-06-12_at_14.36.03_(7).jpg'].map((src, i) => <motion.div key={i} onClick={() => setSelectedImage(src)} initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: i * 0.1
            }} className="aspect-video bg-[#0a0604] border border-[#D4AF37]/20 relative overflow-hidden cursor-pointer group" data-id="element-7534">
                  <img src={src} alt="Progress" className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700" data-id="element-7535" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center" data-id="element-7536">
                    <span className="opacity-0 group-hover:opacity-100 text-white/90 text-xs tracking-widest uppercase font-light transition-opacity duration-500" data-id="element-7537">
                      Enlarge
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 12. Pathway — Bramalea pilot */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7538">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7539" />
          <PunjabiPlacard word="ਸਿੱਖਿਆ" translation="Education" data-id="element-7540" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16" data-id="element-7541">
            {/* Left: the story */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7542">
              <span className="inline-flex items-center gap-2 text-[#D4AF37] text-[0.65rem] tracking-[0.3em] uppercase font-light mb-5" data-id="element-7543">
                <span className="w-8 h-px bg-[#D4AF37]/60" data-id="element-7544" />
                The Pilot
              </span>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-[1.05] mb-6" data-id="element-7545">
                We start where it's
                <br data-id="element-7546" />
                hardest — on purpose.
              </h2>
              <p className="text-lg text-white/70 font-light leading-relaxed mb-6" data-id="element-7547">
                Our first pilot launches at{' '}
                <span className="text-[#D4AF37]" data-id="element-7548">
                  Bramalea Secondary School
                </span>
                , in one of Brampton's least affluent neighbourhoods — the
                students who are too often left out of the story.
              </p>
              <p className="text-lg text-white/70 font-light leading-relaxed mb-8" data-id="element-7549">
                Prove that heritage can light up a classroom{' '}
                <span className="text-[#FDFBF7]" data-id="element-7550">here</span>, and we prove it
                can work anywhere.
              </p>

              {/* Expansion ladder */}
              <div className="flex items-center gap-3 flex-wrap" data-id="element-7551">
                {[{
                label: 'Bramalea',
                sub: '1 school'
              }, {
                label: 'Peel Region',
                sub: 'Regional'
              }, {
                label: 'Canada',
                sub: 'National'
              }].map((step, i) => <div key={step.label} className="flex items-center gap-3" data-id="element-7552">
                    <div className={`px-4 py-2 border ${i === 0 ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/25 bg-black/30'}`} data-id="element-7553">
                      <span className={`block text-sm font-serif ${i === 0 ? 'text-[#D4AF37]' : 'text-white/80'}`} data-id="element-7554">
                        {step.label}
                      </span>
                      <span className="block text-[0.6rem] tracking-widest uppercase text-white/40 font-light" data-id="element-7555">
                        {step.sub}
                      </span>
                    </div>
                    {i < 2 && <ArrowRight className="w-4 h-4 text-[#D4AF37]/50 flex-shrink-0" data-id="element-7556" />}
                  </div>)}
              </div>
            </motion.div>

            {/* Right: the school + quote */}
            <motion.div className="w-full" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-7557">
              <div className="relative overflow-hidden border border-[#D4AF37]/20 shadow-2xl" data-id="element-7558">
                <img src="https://cdn.magicpatterns.com/uploads/tt3kwmA1Jc3p3kSKo745wm/pasted-image.webp" alt="Bramalea Secondary School, 510 Balmoral Drive, Brampton" className="w-full h-56 sm:h-64 object-cover" data-id="element-7559" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" data-id="element-7560" />
                <div className="absolute bottom-0 left-0 p-4" data-id="element-7561">
                  <p className="text-[#FDFBF7] text-sm font-serif" data-id="element-7562">
                    Bramalea Secondary School
                  </p>
                  <p className="text-white/50 text-[0.65rem] tracking-widest uppercase font-light" data-id="element-7563">
                    Brampton, Ontario
                  </p>
                </div>
              </div>

              <div className="border-l-[2px] border-[#D4AF37]/50 pl-6 mt-8 relative" data-id="element-7564">
                <div className="absolute -left-[1px] top-0 w-[2px] h-10 bg-[#D4AF37]" data-id="element-7565" />
                <p className="font-serif text-xl sm:text-2xl text-[#D4AF37] mb-4 leading-relaxed italic" data-id="element-7566">
                  "Our community does not get enough space in historical
                  education. This is a powerful opportunity."
                </p>
                <p className="text-white/40 text-[0.65rem] tracking-widest uppercase font-light" data-id="element-7567">
                  — Principal, Bramalea Secondary School
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 13. Treasury Cards Fan */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7568">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7569" />
          <PunjabiPlacard word="ਖ਼ਜ਼ਾਨਾ" translation="Treasury" data-id="element-7570" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full flex flex-col items-center justify-center h-full py-4" data-id="element-7571">
            <motion.div className="text-center mb-2" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7572">
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-none mb-2 tracking-wide uppercase" data-id="element-7573">
                Treasures of the{' '}
                <span className="text-[#D4AF37]" data-id="element-7574">Sikh Empire</span>
              </h2>
              <p className="text-xs sm:text-sm text-white/60 tracking-[0.2em] uppercase font-light" data-id="element-7575">
                Recovering the lost stories of the Sikh Kingdoms.
              </p>
            </motion.div>

            <TreasuryFan cards={[{
            src: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
            name: 'Hari Singh Nalwa',
            desc: 'The most feared general of the Sikh Khalsa Army, Hari Singh Nalwa pushed the empire to the Khyber Pass — the first commander in centuries to subdue the Afghan frontier. His name alone was said to quiet restless children across the borderlands. He fell at Jamrud in 1837, defending the gateway he had won.',
            stats: [{
              label: 'Rarity',
              value: 'Legendary'
            }, {
              label: 'Type',
              value: 'Figure'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
            name: 'Harmandir Sahib',
            desc: 'The golden spiritual heart of the Sikh faith, rising from the sacred Amritsar pool. Maharaja Ranjit Singh gilded its upper floors in gold and marble, giving it the name the world knows: the Golden Temple. Open on all four sides, it welcomes every soul, of every faith, to its langar and its light.',
            stats: [{
              label: 'Rarity',
              value: 'Mythic'
            }, {
              label: 'Type',
              value: 'Place'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/jyrMYyNM9JskmMzEnBrLTw/Koh-i-noor_Armlet.png',
            name: 'Koh-i-Noor Armlet',
            desc: 'The Koh-i-Noor — “Mountain of Light” — the most storied diamond on earth, worn by Ranjit Singh strapped to his upper arm in this jeweled armlet. After his death the British seized it as a spoil of annexation and set it into the Crown Jewels, where it remains a symbol of all that was taken.',
            stats: [{
              label: 'Rarity',
              value: 'Mythic'
            }, {
              label: 'Type',
              value: 'Ornament'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/itiARdFbZcdkmdrUEtYH65/Golden_Throne_of_Lahore.png',
            name: 'Golden Throne of Lahore',
            desc: 'The hexagonal throne of Maharaja Ranjit Singh, crafted by goldsmith Hafez Muhammad Multani from wood and resin sheathed in pure gold. Its lotus-petal base lifted the Lion of Punjab above his court. Carried off after the empire fell, it now sits behind glass in a London museum, far from the Lahore Darbar it once crowned.',
            stats: [{
              label: 'Rarity',
              value: 'Legendary'
            }, {
              label: 'Type',
              value: 'Artifact'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
            name: 'Ranjit Singh',
            desc: 'The Sher-e-Punjab — the Lion of Punjab — who united the warring misls into a single sovereign empire by 1801. Blind in one eye and unable to read, he forged a court where Sikhs, Muslims, and Hindus rose on merit alone. For forty years no foreign power dared cross the Sutlej while he reigned.',
            stats: [{
              label: 'Rarity',
              value: 'Legendary'
            }, {
              label: 'Type',
              value: 'Ruler'
            }]
          }]} data-id="element-7576" />

            <motion.div className="text-center mt-10 max-w-3xl mx-auto" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.3
          }} data-id="element-7577">
              <div className="flex items-center justify-center gap-4 mb-3" data-id="element-7578">
                <div className="w-12 h-px bg-[#D4AF37]/30" data-id="element-7579" />
                <Star className="w-4 h-4 text-[#D4AF37]" data-id="element-7580" />
                <div className="w-12 h-px bg-[#D4AF37]/30" data-id="element-7581" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] mb-2" data-id="element-7582">
                Real History.{' '}
                <span className="text-[#D4AF37]" data-id="element-7583">Real Connection.</span>
              </h3>
              <p className="text-base sm:text-lg text-white/80 font-serif italic leading-relaxed" data-id="element-7584">
                "A digital bridge to the physical past. Every artifact and
                figure is <span className="text-[#D4AF37]" data-id="element-7585">real history</span>.
                You aren't just playing a game — you are{' '}
                <span className="text-[#D4AF37]" data-id="element-7586">recovering a lost legacy</span>{' '}
                and building a modern Sangat."
              </p>
            </motion.div>
          </div>
        </section>

        {/* 13b. Daily Packet */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7587">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7588" />
          <PunjabiPlacard word="ਰੋਜ਼ਾਨਾ" translation="Daily" data-id="element-7589" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 w-full flex flex-col items-center" data-id="element-7590">
            <motion.div className="text-center mb-16" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7591">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] mb-6 tracking-wide uppercase" data-id="element-7592">
                The Daily <span className="text-[#D4AF37]" data-id="element-7593">Packet</span>
              </h2>
              <p className="text-lg text-white/70 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-7594">
                Every 24 hours, a sealed leather envelope arrives from the
                Darbar. Open it to reveal new artifacts, and answer the optional
                Court Question for bonus influence.
              </p>
            </motion.div>

            <div className="mb-16 relative h-64 flex items-center justify-center" data-id="element-7595">
              <DailyPacketAnimation data-id="element-7596" />
            </div>

            <motion.div className="text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-7597">
              <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-7598">
                Pokémon meets Magic: The Gathering meets Sikh history.
              </h3>
              <p className="text-lg text-white/60 font-light" data-id="element-7599">
                Students learn by collecting, unlocking, and competing.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 14. School to Show */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7600">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7601" />
          <PunjabiPlacard word="ਸਫ਼ਰ" translation="Journey" data-id="element-7602" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7603">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7604">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-12" data-id="element-7605">
                First they unlock the story.
                <br data-id="element-7606" />
                <span className="text-[#D4AF37]" data-id="element-7607">
                  Then they step inside it.
                </span>
              </h2>
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs tracking-[0.2em] uppercase font-light text-white/50" data-id="element-7608">
                <span data-id="element-7609">School</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7610">→</span>
                <span data-id="element-7611">Cards</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7612">→</span>
                <span data-id="element-7613">Bus</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7614">→</span>
                <span className="text-[#D4AF37]" data-id="element-7615">Immersive Show</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Sneak Peek (moved after School to Show) */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7616">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7617" />
          <PunjabiPlacard word="ਝਲਕ" translation="Glimpse" data-id="element-7618" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 text-center" data-id="element-7619">
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7620">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-8" data-id="element-7621">
                A glimpse of the experience.
              </h2>
              <div className="aspect-video bg-black border border-[#D4AF37]/20 shadow-[0_0_50px_rgba(212,175,55,0.1)] relative p-1" data-id="element-7622">
                <iframe src="https://www.youtube.com/embed/pW6rFd_Td0o?controls=0&rel=0&modestbranding=1&showinfo=0" title="Sneak Peek" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" allowFullScreen className="w-full h-full" data-id="element-7623" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 15. Access Problem */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7624">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7625" />
          <PunjabiPlacard word="ਪਹੁੰਚ" translation="Access" data-id="element-7626" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7627">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7628">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7629">
                The barrier should not be a bus.
              </h2>
              <p className="text-lg text-white/60 font-light max-w-2xl mx-auto" data-id="element-7630">
                If we want this to reach the next generation, we must remove the
                financial barriers to transportation and access.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 16. First Year Goal */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7631">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7632" />
          <PunjabiPlacard word="ਨਿਸ਼ਾਨਾ" translation="Target" data-id="element-7633" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7634">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7635">
              <h2 className="font-serif text-7xl sm:text-8xl text-[#FDFBF7] leading-none mb-4" data-id="element-7636">
                12,000
              </h2>
              <p className="text-xl tracking-[0.3em] uppercase text-[#D4AF37] font-light mb-8" data-id="element-7637">
                Students in Year One
              </p>
              <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/50 to-transparent mx-auto" data-id="element-7638" />
            </motion.div>
          </div>
        </section>

        {/* 17. The Ask — Sponsor a Bus */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" data-id="element-7639">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7640" />
          <PunjabiPlacard word="ਸੇਵਾ" translation="Service" data-id="element-7641" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full" data-id="element-7642">
            {/* Header */}
            <motion.div className="text-center mb-10 sm:mb-12" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7643">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7644">
                The Ask
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-5" data-id="element-7645">
                Sponsor a bus. <br className="hidden sm:block" data-id="element-7646" />
                <span className="text-[#D4AF37]" data-id="element-7647">
                  Carry a generation forward.
                </span>
              </h2>
              <p className="text-base sm:text-lg text-white/65 font-light max-w-2xl mx-auto" data-id="element-7648">
                In partnership with the{' '}
                <span className="text-[#FDFBF7]" data-id="element-7649">
                  Sikh Foundation of Canada
                </span>
                , donors fund buses that bring students across the GTA to the
                experience — and receive an official tax receipt.
              </p>
            </motion.div>

            {/* Sponsorship options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8" data-id="element-7650">
              {/* Bus Sponsor — hero */}
              <motion.div className="bg-black/40 border border-[#D4AF37]/40 rounded-2xl p-7 backdrop-blur-md relative overflow-hidden shadow-[0_0_40px_rgba(212,175,55,0.12)]" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} viewport={{
              once: true
            }} data-id="element-7651">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#D4AF37]" data-id="element-7652" />
                <Bus className="w-8 h-8 text-[#D4AF37] mb-4" data-id="element-7653" />
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7654">
                  Sponsor a Bus
                </h3>
                <p className="text-white/60 font-light text-sm leading-relaxed mb-4" data-id="element-7655">
                  Fund transportation for a full classroom of students —
                  removing the single biggest barrier to access.
                </p>
                <div className="flex items-center gap-2 text-[#D4AF37] text-xs tracking-widest uppercase" data-id="element-7656">
                  <Receipt className="w-4 h-4" data-id="element-7657" />
                  Tax receipt provided
                </div>
              </motion.div>

              {/* Other sponsors */}
              <motion.div className="bg-black/30 border border-white/10 rounded-2xl p-7 backdrop-blur-md relative overflow-hidden" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.35
            }} viewport={{
              once: true
            }} data-id="element-7658">
                <div className="absolute top-0 left-0 w-full h-1 bg-white/20" data-id="element-7659" />
                <HeartHandshake className="w-8 h-8 text-[#D4AF37] mb-4" data-id="element-7660" />
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7661">
                  Become a Sponsor
                </h3>
                <p className="text-white/60 font-light text-sm leading-relaxed mb-4" data-id="element-7662">
                  Underwrite the experience, technology, or a school cohort.
                  Flexible sponsorships available for partners who want to do
                  more.
                </p>
                <div className="flex items-center gap-2 text-[#D4AF37] text-xs tracking-widest uppercase" data-id="element-7663">
                  <Receipt className="w-4 h-4" data-id="element-7664" />
                  Tax receipt provided
                </div>
              </motion.div>
            </div>

            {/* Foundation reassurance */}
            <motion.p className="text-center text-white/45 font-light text-xs sm:text-sm max-w-2xl mx-auto" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} viewport={{
            once: true
          }} data-id="element-7665">
              All donations are processed through the Sikh Foundation of Canada
              — a registered charity promoting Sikh values, history, and
              heritage, and empowering Sikh-Canadian youth.
            </motion.p>
          </div>
        </section>

        {/* 17a2. The Perfect Partner — Sikh Foundation of Canada */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" data-id="element-7666">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7667" />
          <PunjabiPlacard word="ਸਾਂਝ" translation="Partnership" data-id="element-7668" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7669">
            {/* Header */}
            <motion.div className="text-center mb-10 sm:mb-12" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7670">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7671">
                How Do We Connect?
              </p>
              <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-5" data-id="element-7672">
                The Sikh Foundation of Canada <br className="hidden sm:block" data-id="element-7673" />
                <span className="text-[#D4AF37]" data-id="element-7674">is the perfect partner.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/65 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-7675">
                They didn't just attend the legacy — they{' '}
                <span className="text-[#FDFBF7]" data-id="element-7676">created it.</span> As the
                original producers of{' '}
                <span className="text-[#FDFBF7] italic" data-id="element-7677">
                  Arts of the Sikh Kingdoms
                </span>{' '}
                at the ROM, their mission and ours are one continuous thread.
              </p>
            </motion.div>

            {/* Mission pillars */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10" data-id="element-7678">
              {[{
              icon: GraduationCap,
              title: 'Education & Mentorship',
              detail: 'University scholarships for exceptional Sikh-Canadian students, plus the Nurturing Excellence mentorship program.'
            }, {
              icon: Palette,
              title: 'Art & Heritage',
              detail: 'Books, publications, and exhibitions — including the dedicated Gallery of Sikh Art & Culture at the ROM.'
            }, {
              icon: HeartHandshake,
              title: 'Community Building',
              detail: 'Celebrating equality, seva, and compassion through events like the annual Sikh Centennial Gala.'
            }].map((c, i) => <motion.div key={c.title} className="bg-black/30 border border-[#D4AF37]/15 rounded-2xl p-6 backdrop-blur-sm" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.15 + i * 0.1
            }} viewport={{
              once: true
            }} data-id="element-7679">
                  <c.icon className="w-8 h-8 text-[#D4AF37] mb-4" data-id="element-7680" />
                  <h3 className="font-serif text-xl text-[#FDFBF7] mb-2 leading-tight" data-id="element-7681">
                    {c.title}
                  </h3>
                  <p className="text-white/55 font-light text-sm leading-relaxed" data-id="element-7682">
                    {c.detail}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 17b. Why Ash Singh */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7683">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7684" />
          <PunjabiPlacard word="ਭਰੋਸਾ" translation="Trust" data-id="element-7685" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full py-8" data-id="element-7686">
            {/* Name + thesis */}
            <motion.div className="text-center mb-10 sm:mb-12" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7687">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7688">
                Why Me
              </p>
              <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-4" data-id="element-7689">
                A lifetime of small steps <br className="hidden sm:block" data-id="element-7690" />
                <span className="text-[#D4AF37]" data-id="element-7691">that led me here.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/60 font-light max-w-xl mx-auto" data-id="element-7692">
                I've spent decades in service of Sikh education, technology, and
                culture. <span className="text-[#FDFBF7]" data-id="element-7693">— Ash Singh</span>
              </p>
            </motion.div>

            {/* Credential grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7694">
              {[{
              icon: Globe,
              title: '65M+ Reached',
              detail: 'Founder of SikhiWiki & Turbanizer'
            }, {
              icon: GraduationCap,
              title: 'Global Professor',
              detail: 'INSEAD · Ivey · Wharton'
            }, {
              icon: Landmark,
              title: 'Family Legacy',
              detail: 'Arts of the Sikh Kingdoms'
            }, {
              icon: HandCoins,
              title: 'Sevadar',
              detail: 'Sikh Art & Culture Gallery, ROM'
            }, {
              icon: Users,
              title: 'Experience Producer',
              detail: 'Large-scale community events & TV'
            }, {
              icon: Sparkles,
              title: 'Builder',
              detail: 'Startup founder, coder & AI builder'
            }].map((c, i) => <motion.div key={c.title} className="bg-black/30 border border-[#D4AF37]/15 rounded-xl p-5 backdrop-blur-sm flex items-start gap-4" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1 + i * 0.08
            }} viewport={{
              once: true
            }} data-id="element-7695">
                  <c.icon className="w-6 h-6 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-7696" />
                  <div data-id="element-7697">
                    <p className="text-[#FDFBF7] font-serif text-lg leading-tight mb-1" data-id="element-7698">
                      {c.title}
                    </p>
                    <p className="text-white/55 font-light text-xs sm:text-sm leading-snug" data-id="element-7699">
                      {c.detail}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 17c. Q&A: ROM vs Immersive */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" data-id="element-7700">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7701" />
          <PunjabiPlacard word="ਸਵਾਲ" translation="Questions" data-id="element-7702" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 w-full" data-id="element-7703">
            <motion.div className="text-center mb-8 sm:mb-10" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7704">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3" data-id="element-7705">
                Not Competition
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#FDFBF7] leading-tight max-w-4xl mx-auto mb-4" data-id="element-7706">
                Two Halves of{' '}
                <span className="text-[#D4AF37]" data-id="element-7707">One Journey</span>
              </h2>
              <p className="text-white/70 font-light text-sm sm:text-base max-w-2xl mx-auto leading-relaxed" data-id="element-7708">
                Same audience. Different role in bringing a generation to Sikh
                heritage.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1.25fr] gap-5 md:gap-4 items-center mb-8" data-id="element-7709">
              {/* Doorway Column — the entry point (left) */}
              <motion.div className="bg-black/30 border border-[#D4AF37]/20 rounded-2xl p-6 sm:p-7 backdrop-blur-md relative overflow-hidden" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 1,
              delay: 0.2
            }} viewport={{
              once: true
            }} data-id="element-7710">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#D4AF37]/40" data-id="element-7711" />
                <span className="text-[#D4AF37]/60 text-[0.65rem] tracking-[0.3em] uppercase block mb-2" data-id="element-7712">
                  The Doorway
                </span>
                <h3 className="font-serif text-xl sm:text-2xl text-[#D4AF37] mb-4 pb-4 border-b border-[#D4AF37]/15" data-id="element-7713">
                  Immersive Exhibit
                </h3>
                <ul className="space-y-3 text-sm text-white/75 font-light" data-id="element-7714">
                  <li className="flex items-start gap-3" data-id="element-7715">
                    <span className="text-[#D4AF37]/50 mt-1 shrink-0" data-id="element-7716">•</span>
                    <span data-id="element-7717">Experiential &amp; national in reach</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-7718">
                    <span className="text-[#D4AF37]/50 mt-1 shrink-0" data-id="element-7719">•</span>
                    <span data-id="element-7720">Activates sponsors &amp; ticket buyers</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-7721">
                    <span className="text-[#D4AF37]/50 mt-1 shrink-0" data-id="element-7722">•</span>
                    <span data-id="element-7723">The on-ramp — where the journey begins</span>
                  </li>
                </ul>
              </motion.div>

              {/* Connector arrow */}
              <motion.div className="flex md:flex-col items-center justify-center text-[#D4AF37]" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} transition={{
              duration: 0.8,
              delay: 0.45
            }} viewport={{
              once: true
            }} data-id="element-7724">
                <div className="w-10 h-10 rounded-full bg-[#0a0604] border border-[#D4AF37]/50 flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.25)]" data-id="element-7725">
                  <ArrowRight className="w-5 h-5 text-[#D4AF37] rotate-90 md:rotate-0" data-id="element-7726" />
                </div>
              </motion.div>

              {/* Home Column — the destination (right, emphasized) */}
              <motion.div className="bg-black/50 border-2 border-[#D4AF37]/60 rounded-2xl p-7 sm:p-9 backdrop-blur-md relative overflow-hidden shadow-[0_0_60px_rgba(212,175,55,0.18)]" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 1,
              delay: 0.5
            }} viewport={{
              once: true
            }} data-id="element-7727">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-[#D4AF37]" data-id="element-7728" />
                <div className="flex items-center gap-3 mb-2" data-id="element-7729">
                  <Landmark className="w-6 h-6 text-[#D4AF37] shrink-0" data-id="element-7730" />
                  <span className="text-[#D4AF37] text-[0.7rem] tracking-[0.3em] uppercase" data-id="element-7731">
                    The Home
                  </span>
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-5 pb-5 border-b border-[#D4AF37]/25" data-id="element-7732">
                  ROM Gallery
                </h3>
                <ul className="space-y-4 text-base sm:text-lg text-white/85 font-light" data-id="element-7733">
                  <li className="flex items-start gap-3" data-id="element-7734">
                    <span className="text-[#D4AF37]/70 mt-1.5 shrink-0" data-id="element-7735">•</span>
                    <span data-id="element-7736">Permanent &amp; scholarly</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-7737">
                    <span className="text-[#D4AF37]/70 mt-1.5 shrink-0" data-id="element-7738">•</span>
                    <span data-id="element-7739">Earns lifelong legacy patrons</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-7740">
                    <span className="text-[#D4AF37]/70 mt-1.5 shrink-0" data-id="element-7741">•</span>
                    <span data-id="element-7742">The destination — where the journey arrives</span>
                  </li>
                </ul>
              </motion.div>
            </div>

            <motion.div className="text-center bg-white/5 border border-white/10 rounded-xl p-6 max-w-3xl mx-auto" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.6
          }} viewport={{
            once: true
          }} data-id="element-7743">
              <p className="font-serif italic text-base sm:text-lg text-[#FDFBF7]" data-id="element-7744">
                We create the <span className="text-[#D4AF37]" data-id="element-7745">reach</span>. The
                gallery turns it into <span className="text-white" data-id="element-7746">depth</span>.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 17d. The Strategic Pipeline — Spark → Scale → Destination */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" data-id="element-7747">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7748" />
          <PunjabiPlacard word="ਰਾਹ" translation="The Path" data-id="element-7749" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7750">
            {/* Header */}
            <motion.div className="text-center mb-8 sm:mb-10" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7751">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7752">
                How It All Connects
              </p>
              <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-5" data-id="element-7753">
                One Pipeline.{' '}
                <span className="text-[#D4AF37]" data-id="element-7754">National Scale.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/65 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-7755">
                The immersive exhibit isn't the finish line — it's the{' '}
                <span className="text-[#FDFBF7]" data-id="element-7756">starting point</span>. It feeds
                a national engine that ends, deliberately, at the door of the
                ROM gallery the Foundation built.
              </p>
            </motion.div>

            {/* The 3-stage funnel */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-3 items-stretch relative mb-8" data-id="element-7757">
              {[{
              icon: Sparkles,
              step: 'Stage 01',
              title: 'The Spark',
              detail: 'The immersive exhibit is the emotional first encounter — a felt, unforgettable entry point that makes Sikh history matter to anyone, no museum visit required.'
            }, {
              icon: Globe,
              step: 'Stage 02',
              title: 'The National Engine',
              detail: 'Our schools program and the digital Treasures of the Sikh Empire collectible cards carry that spark across Canada — classroom by classroom, screen by screen, turning one exhibit into a movement.'
            }, {
              icon: Landmark,
              step: 'Stage 03',
              title: 'The Destination',
              detail: 'A generation now invested in their heritage arrives where depth lives — the ROM Gallery of Sikh Art & Culture — becoming the Foundation\u2019s next wave of patrons.'
            }].map((c, i) => <Fragment key={c.title} data-id="element-7758">
                  <motion.div className={`rounded-2xl p-6 backdrop-blur-sm relative overflow-hidden border ${i === 2 ? 'bg-black/45 border-[#D4AF37]/45 shadow-[0_0_40px_rgba(212,175,55,0.12)]' : 'bg-black/30 border-[#D4AF37]/15'}`} initial={{
                opacity: 0,
                y: 24
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 0.2 + i * 0.15
              }} viewport={{
                once: true
              }} data-id="element-7759">
                    <div className="flex items-center gap-3 mb-4" data-id="element-7760">
                      <c.icon className="w-7 h-7 text-[#D4AF37] shrink-0" data-id="element-7761" />
                      <span className="text-[#D4AF37]/70 text-[0.65rem] tracking-[0.25em] uppercase" data-id="element-7762">
                        {c.step}
                      </span>
                    </div>
                    <h3 className="font-serif text-xl text-[#FDFBF7] mb-2 leading-tight" data-id="element-7763">
                      {c.title}
                    </h3>
                    <p className="text-white/55 font-light text-sm leading-relaxed" data-id="element-7764">
                      {c.detail}
                    </p>
                  </motion.div>
                  {i < 2 && <div className="hidden md:flex absolute top-1/2 z-20 pointer-events-none" style={{
                left: i === 0 ? '33.333%' : '66.666%',
                transform: 'translate(-50%, -50%)'
              }} data-id="element-7765">
                      <div className="w-9 h-9 rounded-full bg-[#0a0604] border border-[#D4AF37]/50 flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.25)]" data-id="element-7766">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37]" data-id="element-7767" />
                      </div>
                    </div>}
                </Fragment>)}
            </div>
          </div>
        </section>

        {/* 18. National Moments */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7768">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7769" />
          <PunjabiPlacard word="ਲਹਿਰ" translation="Movement" data-id="element-7770" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center" data-id="element-7771">
            <motion.p className="text-[#D4AF37] text-xs sm:text-sm tracking-[0.4em] uppercase mb-6" initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7772">
              From the Distillery to the Nation
            </motion.p>
            <motion.h2 className="font-serif text-5xl sm:text-6xl md:text-7xl text-[#FDFBF7] leading-[1.05] mb-6" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            delay: 0.1
          }} viewport={{
            once: true
          }} data-id="element-7773">
              This becomes a <br className="hidden sm:block" data-id="element-7774" />
              <span className="text-[#D4AF37]" data-id="element-7775">Canadian cultural moment.</span>
            </motion.h2>
            <motion.p className="text-lg text-white/60 font-light max-w-2xl mx-auto mb-16" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.3
          }} viewport={{
            once: true
          }} data-id="element-7776">
              A two-stage path from provincial to national recognition — Sikh
              history taking its place on Canada’s biggest stages.
            </motion.p>

            {/* Connected milestone timeline */}
            <div className="relative flex flex-col sm:flex-row items-stretch justify-center gap-8 sm:gap-4" data-id="element-7777">
              {/* connector line */}
              <div className="hidden sm:block absolute top-7 left-[18%] right-[18%] h-px bg-gradient-to-r from-[#D4AF37]/0 via-[#D4AF37]/50 to-[#D4AF37]/0" data-id="element-7778" />

              {[{
              date: 'Nov 2026',
              icon: Landmark,
              title: 'Provincial Moment',
              body: 'Hosted with the Premier of Ontario & Provincial Government.'
            }, {
              date: 'Apr 2027',
              icon: Building2,
              title: 'National Moment',
              body: 'Carried to the Prime Minister & Federal Government in Ottawa.'
            }].map((m, i) => <motion.div key={m.date} className="flex-1 relative flex flex-col items-center" initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.4 + i * 0.2
            }} viewport={{
              once: true
            }} data-id="element-7779">
                  <div className="w-14 h-14 rounded-full bg-[#110c08] border border-[#D4AF37]/50 flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.25)] mb-5 relative z-10" data-id="element-7780">
                    <m.icon className="w-6 h-6 text-[#D4AF37]" data-id="element-7781" />
                  </div>
                  <p className="font-serif text-2xl text-[#D4AF37] mb-2" data-id="element-7782">
                    {m.date}
                  </p>
                  <p className="text-[#FDFBF7] text-sm tracking-widest uppercase mb-2" data-id="element-7783">
                    {m.title}
                  </p>
                  <p className="text-white/60 font-light text-sm max-w-xs" data-id="element-7784">
                    {m.body}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 19. Ask Context */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7785">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7786" />
          <PunjabiPlacard word="ਸਰਪ੍ਰਸਤ" translation="Patron" data-id="element-7787" />

          {/* Ambient glow */}
          <div className="absolute inset-0 z-[1] pointer-events-none bg-[radial-gradient(circle_at_50%_40%,rgba(212,175,55,0.12),transparent_60%)]" data-id="element-7788" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6 py-10" data-id="element-7789">
            <motion.p className="text-[#D4AF37] text-xs sm:text-sm tracking-[0.4em] uppercase mb-6" initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7790">
              For those who remember
            </motion.p>

            <motion.p className="font-serif italic text-xl sm:text-2xl text-white/70 leading-relaxed max-w-2xl mx-auto mb-8" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.4,
            delay: 0.15
          }} viewport={{
            once: true
          }} data-id="element-7791">
              We grew up in classrooms that never told our story — or told it
              wrong. We learned to shrink it down to a paragraph.
            </motion.p>

            <motion.h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-6" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            delay: 0.3
          }} viewport={{
            once: true
          }} data-id="element-7792">
              Now we get to give the next generation{' '}
              <span className="text-[#D4AF37]" data-id="element-7793">the version we deserved.</span>
            </motion.h2>

            <motion.p className="text-lg text-white/60 font-light mb-12 max-w-2xl mx-auto" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} viewport={{
            once: true
          }} data-id="element-7794">
              We have committed{' '}
              <span className="text-[#D4AF37] font-medium" data-id="element-7795">$500,000</span> to
              begin. Become a Founding Patron and help carry it forward.
            </motion.p>

            <div className="grid sm:grid-cols-2 gap-4 text-left max-w-2xl mx-auto" data-id="element-7796">
              {[{
              icon: GraduationCap,
              label: 'Sponsor your school',
              sub: 'Put your name behind the next class to walk in.'
            }, {
              icon: Users,
              label: 'Fund student access',
              sub: 'No child priced out of their own history.'
            }, {
              icon: Sparkles,
              label: 'Production upgrades',
              sub: 'Make the world even more breathtaking.'
            }, {
              icon: Globe,
              label: 'National expansion',
              sub: 'Carry it city to city, coast to coast.'
            }].map((item, i) => <motion.div key={item.label} className="flex items-start gap-3 border border-[#D4AF37]/25 bg-black/40 p-4 backdrop-blur-sm hover:border-[#D4AF37]/60 transition-colors" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.6 + i * 0.12
            }} viewport={{
              once: true
            }} data-id="element-7797">
                  <item.icon className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-7798" />
                  <div data-id="element-7799">
                    <span className="text-[#FDFBF7] text-sm font-medium block" data-id="element-7800">
                      {item.label}
                    </span>
                    <span className="text-white/50 text-xs font-light" data-id="element-7801">
                      {item.sub}
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 20. Ask Tiers */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7802">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7803" />
          <PunjabiPlacard word="ਵਿਰਸਾ" translation="Legacy" data-id="element-7804" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7805">
            <div className="text-center mb-10" data-id="element-7806">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7807">
                Choose your legacy.
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-7808">
              {[{
              price: '$10,000',
              title: 'Patron of the Bus',
              icon: Building2,
              items: ['Sponsor student transportation', 'Recognition on sponsor night', 'Invite a school']
            }, {
              price: '$25,000',
              title: 'Founding School Patron',
              icon: Users,
              items: ['Sponsor a school activation', 'Family banner on your night', 'Recognition inside experience']
            }, {
              price: '$50,000',
              title: 'Legacy Patron',
              icon: Crown,
              items: ['Support access & expansion', 'Premium family recognition', 'Maharaja acknowledgement moment']
            }].map((tier, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: i * 0.1
            }} className="border border-[#D4AF37]/30 bg-[#0a0604]/80 p-6 backdrop-blur-md relative overflow-hidden" data-id="element-7809">
                  <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-7810">
                    <tier.icon className="w-20 h-20 text-[#D4AF37]" data-id="element-7811" />
                  </div>
                  <h3 className="font-serif text-3xl text-[#FDFBF7] mb-2" data-id="element-7812">
                    {tier.price}
                  </h3>
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-6" data-id="element-7813">
                    {tier.title}
                  </p>
                  <ul className="space-y-3" data-id="element-7814">
                    {tier.items.map((item, j) => <li key={j} className="text-white/60 text-sm font-light flex gap-2" data-id="element-7815">
                        <span className="text-[#D4AF37] opacity-50" data-id="element-7816">•</span>{' '}
                        {item}
                      </li>)}
                  </ul>
                </motion.div>)}
            </div>

            <div className="text-center flex flex-col items-center" data-id="element-7817">
              <div className="bg-white p-2 rounded-sm shadow-[0_0_30px_rgba(212,175,55,0.3)] mb-4" data-id="element-7818">
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://typeform.com" alt="Scan to commit" className="w-20 h-20" data-id="element-7819" />
              </div>
              <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-7820">
                Scan to commit
              </p>
              <p className="text-white/40 text-[10px] italic font-light" data-id="element-7821">
                Contributions may be eligible for tax receipts where applicable.
              </p>
            </div>
          </div>
        </section>

        {/* 20b. The Founder Coin */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-10" data-id="element-7822">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7823" />
          <PunjabiPlacard word="ਸਿੱਕਾ" translation="The Coin" data-id="element-7824" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full flex flex-col items-center text-center" data-id="element-7825">
            <motion.div className="mb-6" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7826">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3" data-id="element-7827">
                Limited Edition · Founder Member
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7]" data-id="element-7828">
                The Founder’s Coin.
              </h2>
            </motion.div>

            <motion.div className="w-full" initial={{
            opacity: 0,
            scale: 0.96
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-7829">
              <FounderCoinCarousel coins={[{
              src: 'https://cdn.magicpatterns.com/uploads/iKFJFRzMvdjc7PQr9DfHUw/image.png',
              label: 'Founder No. FM-0001'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/jffPzsfZHnE1yXYeZLtNtQ/image.png',
              label: 'Founder No. FM-0010'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/dznAAvh5j3fw4ifouNnW5D/image.png',
              label: 'Founder No. FM-0011'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/c9HMsd9FKUmBiJVi913QdX/image.png',
              label: 'Founder No. FM-0012'
            }]} data-id="element-7830" />
            </motion.div>

            <motion.p className="text-white/60 font-light text-sm max-w-xl mx-auto mt-6" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.4
          }} viewport={{
            once: true
          }} data-id="element-7831">
              A hand-numbered, limited-edition keepsake — boxed, pouched, and
              certified. Each founder also receives{' '}
              <span className="text-[#D4AF37]" data-id="element-7832">100 invitation coins</span> to
              hand out, inviting their circle into the Lahore Darbar.
            </motion.p>
          </div>
        </section>

        {/* 21. FAQ */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7833">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7834" />
          <PunjabiPlacard word="ਗਿਆਨ" translation="Knowledge" data-id="element-7835" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 w-full" data-id="element-7836">
            <div className="text-center mb-10" data-id="element-7837">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-3" data-id="element-7838">
                Giving &amp; tax, simply.
              </h2>
            </div>

            {/* Cost-after-tax comparison */}
            <div className="border border-[#D4AF37]/30 bg-[#0a0604]/70 p-6 sm:p-8 backdrop-blur-sm mb-6" data-id="element-7839">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6 text-center" data-id="element-7840">
                What it really costs in Ontario
              </p>
              <div className="grid grid-cols-3 gap-3 sm:gap-6" data-id="element-7841">
                {[{
                gift: '$10,000',
                cost: '~$6,000'
              }, {
                gift: '$25,000',
                cost: '~$15,000'
              }, {
                gift: '$50,000',
                cost: '~$30,000'
              }].map(row => <div key={row.gift} className="flex flex-col items-center text-center" data-id="element-7842">
                    <span className="font-serif text-lg sm:text-2xl text-white/50 line-through decoration-[#D4AF37]/40" data-id="element-7843">
                      {row.gift}
                    </span>
                    <ArrowRight className="w-4 h-4 text-[#D4AF37] my-2 rotate-90" data-id="element-7844" />
                    <span className="font-serif text-2xl sm:text-4xl text-[#D4AF37] leading-none" data-id="element-7845">
                      {row.cost}
                    </span>
                    <span className="text-white/50 text-[0.6rem] tracking-widest uppercase mt-2" data-id="element-7846">
                      after-tax cost
                    </span>
                  </div>)}
              </div>
              <p className="text-[10px] text-white/40 italic mt-6 text-center" data-id="element-7847">
                Estimated — charitable credits are non-refundable and depend on
                your personal tax situation.
              </p>
            </div>

            {/* Receipting types — compact */}
            <div className="grid sm:grid-cols-3 gap-4" data-id="element-7848">
              {[{
              k: 'Donation',
              v: 'No benefit received — full charitable receipt.'
            }, {
              k: 'Sponsorship',
              v: 'Marketing value — a business expense, not a receipt.'
            }, {
              k: 'Hybrid',
              v: 'Partial receipt above the value of any benefits.'
            }].map(t => <div key={t.k} className="border border-[#D4AF37]/20 bg-[#0a0604]/50 p-5 backdrop-blur-sm" data-id="element-7849">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2" data-id="element-7850">
                    {t.k}
                  </p>
                  <p className="text-xs text-white/60 font-light leading-relaxed" data-id="element-7851">
                    {t.v}
                  </p>
                </div>)}
            </div>

            <p className="text-[10px] text-white/40 italic mt-6 text-center" data-id="element-7852">
              General information, not tax advice — please confirm with your
              advisor.
            </p>
          </div>
        </section>

        {/* 22. Cartier Collaboration - The Proposal */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" data-id="element-7853">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7854" />
          <PunjabiPlacard word="ਮੇਲ" translation="Union" data-id="element-7855" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full" data-id="element-7856">
            {/* Header */}
            <motion.div className="text-center mb-10 sm:mb-12" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7857">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7858">
                A Once-in-a-Century Partnership
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-5" data-id="element-7859">
                Two houses of legacy. <br className="hidden sm:block" data-id="element-7860" />
                <span className="text-[#D4AF37]" data-id="element-7861">One cultural moment.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/65 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-7862">
                Cartier set the diamonds of the Lahore Treasury. Today, that
                shared history becomes a living collaboration — a chance for the
                world's most storied jeweler to help return Sikh heritage to the
                global stage.
              </p>
            </motion.div>

            {/* What the collaboration unlocks */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8" data-id="element-7863">
              {[{
              icon: Crown,
              title: 'Heritage Reunited',
              detail: 'A narrative that reconnects Cartier with the Maharajas and the jewels of the Lahore Treasury it once set.'
            }, {
              icon: Sparkles,
              title: 'A Global Campaign',
              detail: 'Magazine editorials and billboards bringing the Sikh Empire’s splendor to the streets of the world.'
            }, {
              icon: HeartHandshake,
              title: 'Purpose Built In',
              detail: 'Proceeds support the experience and the schools program — prestige with measurable cultural impact.'
            }].map((c, i) => <motion.div key={c.title} className="bg-black/30 border border-[#D4AF37]/15 rounded-2xl p-6 backdrop-blur-sm" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.15 + i * 0.1
            }} viewport={{
              once: true
            }} data-id="element-7864">
                  <c.icon className="w-8 h-8 text-[#D4AF37] mb-4" data-id="element-7865" />
                  <h3 className="font-serif text-xl text-[#FDFBF7] mb-2 leading-tight" data-id="element-7866">
                    {c.title}
                  </h3>
                  <p className="text-white/55 font-light text-sm leading-relaxed" data-id="element-7867">
                    {c.detail}
                  </p>
                </motion.div>)}
            </div>

            <motion.p className="text-center font-serif italic text-lg sm:text-xl text-[#FDFBF7] max-w-3xl mx-auto" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} viewport={{
            once: true
          }} data-id="element-7868">
              The two empires overlapped for two years.{' '}
              <span className="text-[#D4AF37]" data-id="element-7869">
                The collaboration can last far longer.
              </span>
            </motion.p>
          </div>
        </section>

        {/* 23. Cartier Collaboration - Magazine */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7870">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7871" />
          <PunjabiPlacard word="ਵਿਰਾਸਤ" translation="Legacy" data-id="element-7872" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full grid lg:grid-cols-2 gap-12 items-center" data-id="element-7873">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7874">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7875">
                A Legacy of Cultural Exchange
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6 leading-tight" data-id="element-7876">
                The Sikh Empire & <br data-id="element-7877" />
                <span className="text-[#D4AF37]" data-id="element-7878">The House of Cartier</span>
              </h2>
              <div className="space-y-6 text-white/80 font-light text-lg" data-id="element-7879">
                <p data-id="element-7880">
                  Maharaja Ranjit Singh’s court was globally connected,
                  maintaining diplomatic relations with the King of France.
                  French generals trained the Khalsa army, bringing European
                  military precision to the Punjab.
                </p>
                <p data-id="element-7881">
                  The Sikh Empire collapsed in{' '}
                  <span className="text-[#FDFBF7] font-medium" data-id="element-7882">1849</span>. The
                  House of Cartier was founded in Paris in{' '}
                  <span className="text-[#FDFBF7] font-medium" data-id="element-7883">1847</span>.
                </p>
                <p className="font-serif italic text-xl text-[#D4AF37] border-l-2 border-[#D4AF37]/30 pl-4 py-2" data-id="element-7884">
                  For two years, these two empires overlapped in time.
                </p>
                <p data-id="element-7885">
                  Ever since, Cartier has been the jeweler of choice for the
                  Maharajas, setting the world's most famous diamonds —
                  including those that once belonged to the Lahore Treasury.
                </p>
              </div>
            </motion.div>

            <motion.div className="relative flex justify-center" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-7886">
              <div className="relative w-full max-w-md aspect-[2/3] cursor-pointer group" onClick={() => setSelectedImage('https://cdn.magicpatterns.com/uploads/oQ58wnU7hUNtNqVh7jGTxZ/Gemini_Generated_Image_ep18z9ep18z9ep18.png')} data-id="element-7887">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#D4AF37]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" data-id="element-7888" />
                <img src="https://cdn.magicpatterns.com/uploads/oQ58wnU7hUNtNqVh7jGTxZ/Gemini_Generated_Image_ep18z9ep18z9ep18.png" alt="Cartier Magazine Concept" className="w-full h-full object-cover shadow-[0_0_40px_rgba(0,0,0,0.8)] border border-white/10" data-id="element-7889" />
                <div className="absolute inset-0 border border-[#D4AF37]/30 m-4 pointer-events-none" data-id="element-7890" />
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20" data-id="element-7891">
                  <span className="bg-black/80 text-[#D4AF37] text-[10px] tracking-widest uppercase px-4 py-2 backdrop-blur-sm border border-[#D4AF37]/30" data-id="element-7892">
                    Click to Enlarge
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 24. Cartier Collaboration - Billboard */}
        <section className="w-screen h-screen flex-shrink-0 overflow-hidden relative flex items-center justify-center bg-black" data-id="element-7893">
          <PunjabiPlacard word="ਪ੍ਰਭਾਵ" translation="Impact" data-id="element-7894" />

          {/* Full-bleed billboard image */}
          <motion.div className="absolute inset-0 cursor-pointer group" initial={{
          opacity: 0,
          scale: 1.04
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.4,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} onClick={() => setSelectedImage('https://cdn.magicpatterns.com/uploads/6CMkBmPZYB8UPFpRm8EZSo/Gemini_Generated_Image_z4wlxz4wlxz4wlxz.png')} data-id="element-7895">
            <img src="https://cdn.magicpatterns.com/uploads/6CMkBmPZYB8UPFpRm8EZSo/Gemini_Generated_Image_z4wlxz4wlxz4wlxz.png" alt="Cartier Billboard Concept" className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-[1200ms]" data-id="element-7896" />
            {/* Readability scrims top & bottom */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/70 pointer-events-none" data-id="element-7897" />

            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20" data-id="element-7898">
              <span className="bg-black/80 text-[#D4AF37] text-[10px] tracking-widest uppercase px-4 py-2 backdrop-blur-sm border border-[#D4AF37]/30" data-id="element-7899">
                Click to Enlarge
              </span>
            </div>
          </motion.div>

          {/* Overlaid heading */}
          <motion.div className="relative z-10 text-center px-6 mt-[-40vh] pointer-events-none" initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.3
        }} viewport={{
          once: true
        }} data-id="element-7900">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4 drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]" data-id="element-7901">
              The Vision
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] max-w-3xl mx-auto drop-shadow-[0_4px_16px_rgba(0,0,0,0.95)]" data-id="element-7902">
              Bringing the legacy to the streets of the world.
            </h2>
          </motion.div>
        </section>

        {/* 25. The Lahore Darbar Leadership Lab — MBA capstone */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" data-id="element-7903">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7904" />
          <PunjabiPlacard word="ਅਗਵਾਈ" translation="Leadership" data-id="element-7905" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 w-full flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-7906">
            {/* Left Column: Ivey Brand & Title */}
            <motion.div className="flex-1 text-left w-full" initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-7907">
              <div className="flex items-center gap-6 mb-8" data-id="element-7908">
                <div className="bg-white rounded-md px-5 py-3 inline-flex shadow-[0_8px_30px_rgba(0,0,0,0.5)]" data-id="element-7909">
                  <img src="https://cdn.magicpatterns.com/uploads/8RSDZDCFJC94F2N5gDFaR9/pasted-image.png" alt="Ivey Business School, Western University, Canada" className="h-10 sm:h-12 w-auto object-contain" data-id="element-7910" />
                </div>
                <div className="h-px bg-white/20 flex-1 hidden sm:block" data-id="element-7911"></div>
                <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase hidden sm:block" data-id="element-7912">
                  MBA Capstone
                </p>
              </div>

              <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#FDFBF7] leading-[1.1] mb-6" data-id="element-7913">
                The Lahore Darbar <br data-id="element-7914" />
                <span className="text-[#D4AF37] italic" data-id="element-7915">Leadership Lab</span>
              </h2>

              <div className="flex items-start gap-6 mt-12 pt-8 border-t border-white/10" data-id="element-7916">
                <span className="font-serif text-6xl text-[#D4AF37] leading-none shrink-0" data-id="element-7917">
                  #2
                </span>
                <p className="text-white/70 font-light text-sm sm:text-base leading-relaxed max-w-sm" data-id="element-7918">
                  Ivey is the{' '}
                  <span className="text-white font-medium" data-id="element-7919">
                    second-largest producer of case studies in the world
                  </span>
                  , behind only Harvard. Becoming a capstone here puts Sikh
                  history in front of Canada’s future leaders.
                </p>
              </div>
            </motion.div>

            {/* Right Column: The Case & Imagery */}
            <motion.div className="flex-1 w-full relative" initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-7920">
              {/* Decorative glow */}
              <div className="absolute -inset-10 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-transparent rounded-full blur-3xl z-0 pointer-events-none" data-id="element-7921"></div>

              <div className="relative z-10 bg-black/40 border border-white/10 rounded-2xl p-8 sm:p-10 backdrop-blur-md shadow-2xl" data-id="element-7922">
                <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] leading-snug mb-10" data-id="element-7923">
                  “How do you build institutions strong enough to{' '}
                  <span className="text-[#D4AF37]" data-id="element-7924">
                    outlive a brilliant founder?
                  </span>
                  ”
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-7925">
                  {['https://cdn.magicpatterns.com/uploads/uPbfJuRcUJBTsH767Ydzun/richard-ivey-building-southwest-corner.jpg', 'https://cdn.magicpatterns.com/uploads/eei1VL3A4XPSuCegXUhYvS/145-ellisdon-ivey-school-of-business-edit.jpg'].map(src => <div key={src} className="relative overflow-hidden rounded-lg cursor-pointer group border border-white/5" onClick={() => setSelectedImage(src)} data-id="element-7926">
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500 z-10" data-id="element-7927"></div>
                      <img src={src} alt="Ivey Business School" className="w-full h-32 sm:h-40 object-cover grayscale-[50%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" data-id="element-7928" />
                    </div>)}
                </div>

                <div className="flex items-center justify-between text-[0.65rem] sm:text-xs tracking-[0.2em] uppercase text-white/50 border-t border-white/10 pt-6" data-id="element-7929">
                  <span className="text-white/90" data-id="element-7930">Prepare</span>
                  <span className="text-[#D4AF37]/50" data-id="element-7931">→</span>
                  <span className="text-white/90" data-id="element-7932">Step Inside</span>
                  <span className="text-[#D4AF37]/50" data-id="element-7933">→</span>
                  <span className="text-[#D4AF37]" data-id="element-7934">Advise the Darbar</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-7935">
        {selectedImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 sm:p-8 cursor-pointer" onClick={() => setSelectedImage(null)} data-id="element-7936">
            <motion.img initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain rounded-sm shadow-[0_0_50px_rgba(212,175,55,0.15)]" data-id="element-7937" />
            <div className="absolute top-6 right-6 text-white/50 text-sm font-light tracking-widest uppercase" data-id="element-7938">
              Close
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};